import React, { useState, Component } from 'react';
import { Link2, Share2, FileDown } from 'lucide-react';
interface ShareUtilityProps {
  variant: 'preview' | 'full-top' | 'full-end';
  reportId?: string;
  reportType?: 'self' | 'work' | 'relationship' | 'child';
}
export function ShareUtility({
  variant,
  reportId,
  reportType = 'self'
}: ShareUtilityProps) {
  const [copied, setCopied] = useState(false);
  const handleCopyLink = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const handleWhatsAppShare = () => {
    const url = window.location.href;
    const text = variant === 'full-end' ? 'I wanted to share this diagnostic with you.' : 'Take a look at this.';
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`;
    window.open(whatsappUrl, '_blank');
  };
  const handleDownloadPDF = () => {
    // Placeholder for PDF generation
    console.log('Download PDF:', {
      reportId,
      reportType,
      variant
    });
    // In production, this would trigger PDF generation API
  };
  // VARIANT 1: Preview Page (after hard cutoff)
  if (variant === 'preview') {
    return <div className="mt-16 pt-12 border-t-2 border-[#64748B]/30" data-id="element-240">
        <div className="border border-[#64748B]/40 bg-[#111214] p-8 space-y-6" data-id="element-241">
          <div className="space-y-3" data-id="element-242">
            <h3 className="text-lg font-medium text-[#F5F5F5]" data-id="element-243">
              Share This Diagnostic
            </h3>
            <p className="text-sm text-[#F5F5F5]/70 leading-relaxed" data-id="element-244">
              Want to show this to someone who knows you? This is your private
              diagnostic link. You control who sees it.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3" data-id="element-245">
            <button onClick={handleCopyLink} className="flex items-center justify-center gap-2 px-6 py-3 bg-[#F5F5F5] text-[#0A0A0A] text-sm font-medium hover:bg-gray-200 transition-colors" data-id="element-246">
              <Link2 className="w-4 h-4" data-id="element-247" />
              {copied ? 'Link copied' : 'Copy private link'}
            </button>

            <button onClick={handleWhatsAppShare} className="flex items-center justify-center gap-2 px-6 py-3 border-2 border-[#F5F5F5] text-[#F5F5F5] text-sm font-medium hover:bg-[#F5F5F5] hover:text-[#0A0A0A] transition-colors" data-id="element-248">
              <Share2 className="w-4 h-4" data-id="element-249" />
              Share on WhatsApp
            </button>

            <button onClick={handleDownloadPDF} className="flex items-center justify-center gap-2 px-6 py-3 border-2 border-[#F5F5F5] text-[#F5F5F5] text-sm font-medium hover:bg-[#F5F5F5] hover:text-[#0A0A0A] transition-colors" data-id="element-250">
              <FileDown className="w-4 h-4" data-id="element-251" />
              Download Preview PDF
            </button>
          </div>
        </div>
      </div>;
  }
  // VARIANT 2: Full Report Top (utility row)
  if (variant === 'full-top') {
    return <div className="mb-12 pb-8 border-b-2 border-[#64748B]/30" data-id="element-252">
        <div className="border border-[#64748B]/40 bg-[#111214] p-6" data-id="element-253">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-254">
            <div className="space-y-1" data-id="element-255">
              <p className="text-sm font-medium text-[#F5F5F5]" data-id="element-256">
                Save or Share
              </p>
              <p className="text-xs text-[#F5F5F5]/60" data-id="element-257">
                This is your private diagnostic. Share only if you want to.
              </p>
            </div>

            <div className="flex flex-wrap gap-2" data-id="element-258">
              <button onClick={handleCopyLink} className="flex items-center gap-2 px-4 py-2 bg-[#F5F5F5] text-[#0A0A0A] text-xs font-medium hover:bg-gray-200 transition-colors" data-id="element-259">
                <Link2 className="w-3.5 h-3.5" data-id="element-260" />
                {copied ? 'Copied' : 'Copy link'}
              </button>

              <button onClick={handleDownloadPDF} className="flex items-center gap-2 px-4 py-2 border-2 border-[#F5F5F5] text-[#F5F5F5] text-xs font-medium hover:bg-[#F5F5F5] hover:text-[#0A0A0A] transition-colors" data-id="element-261">
                <FileDown className="w-3.5 h-3.5" data-id="element-262" />
                Download PDF
              </button>

              <button onClick={handleWhatsAppShare} className="flex items-center gap-2 px-4 py-2 border-2 border-[#F5F5F5] text-[#F5F5F5] text-xs font-medium hover:bg-[#F5F5F5] hover:text-[#0A0A0A] transition-colors" data-id="element-263">
                <Share2 className="w-3.5 h-3.5" data-id="element-264" />
                Share
              </button>
            </div>
          </div>
        </div>
      </div>;
  }
  // VARIANT 3: Full Report End (after final mirror)
  if (variant === 'full-end') {
    return <div className="mt-16 pt-12 border-t-2 border-[#64748B]/30" data-id="element-265">
        <div className="border border-[#64748B]/40 bg-[#111214] p-8 space-y-8" data-id="element-266">
          <div className="space-y-4" data-id="element-267">
            <h3 className="text-xl font-medium text-[#F5F5F5]" data-id="element-268">
              Share This Diagnostic
            </h3>
            <div className="space-y-3" data-id="element-269">
              <p className="text-base text-[#F5F5F5]/80 leading-relaxed" data-id="element-270">
                If this felt uncomfortably accurate, one person in your life
                will recognize you in this too.
              </p>
              <p className="text-sm text-[#F5F5F5]/60 leading-relaxed" data-id="element-271">
                Most people share this with one person: a partner, a close
                friend, or someone who has seen these patterns up close.
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3" data-id="element-272">
            <button onClick={handleCopyLink} className="flex items-center justify-center gap-2 px-6 py-3 bg-[#F5F5F5] text-[#0A0A0A] text-sm font-medium hover:bg-gray-200 transition-colors" data-id="element-273">
              <Link2 className="w-4 h-4" data-id="element-274" />
              {copied ? 'Link copied' : 'Copy private link'}
            </button>

            <button onClick={handleWhatsAppShare} className="flex items-center justify-center gap-2 px-6 py-3 border-2 border-[#F5F5F5] text-[#F5F5F5] text-sm font-medium hover:bg-[#F5F5F5] hover:text-[#0A0A0A] transition-colors" data-id="element-275">
              <Share2 className="w-4 h-4" data-id="element-276" />
              Share on WhatsApp
            </button>

            <button onClick={handleDownloadPDF} className="flex items-center justify-center gap-2 px-6 py-3 border-2 border-[#F5F5F5] text-[#F5F5F5] text-sm font-medium hover:bg-[#F5F5F5] hover:text-[#0A0A0A] transition-colors" data-id="element-277">
              <FileDown className="w-4 h-4" data-id="element-278" />
              Download PDF
            </button>
          </div>
        </div>
      </div>;
  }
  return null;
}